import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  EmailRecipientsInput,
} from '@civilgrid/ui/components';
import { cn } from '@civilgrid/ui/utils';
import {
  isValidRecipientEmail,
  MAX_CC_RECIPIENTS,
  recipientAddressRules,
} from '@civilgrid/utils/src/utilities/emailRecipients';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { useExportEmailCcEnabled } from '~/hooks/useExportEmailCcEnabled';

const DEFAULT_SUBMIT_LABEL = 'Send email';
const HALF_TYPED_CC_TOOLTIP = 'Finish or clear the email address you are typing.';
// Above the app's highest dialog layer, the `z-[110]` components2 Modal.
const OVERLAY_LAYER = 'z-[120]';
const CONTENT_LAYER = 'z-[130]';
const CONTENT_WIDTH = { default: '', wide: 'w-auto max-w-[75vw]' } as const;

export interface ExportSummaryRow {
  label: string;
  value: ReactNode;
}

export interface SendEmailModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  /** Prefixes every `data-testid` the modal renders. */
  testIdPrefix: string;
  submitLabel?: string;
  submitDisabled?: boolean;
  size?: keyof typeof CONTENT_WIDTH;
  summary?: ExportSummaryRow[];
  acknowledgement?: ReactNode;
  /**
   * Receives the cc recipients; returning false or throwing keeps the modal open.
   *
   * When the modal has nothing to show it closes after `onSend` settles, whatever it returns.
   */
  onSend: (ccEmails: string[]) => unknown;
  children?: ReactNode;
  /** Overrides focus on close, e.g. when the opener was a menu item. */
  onCloseAutoFocus?: (event: Event) => void;
}

export function SendEmailModal({
  open,
  onOpenChange,
  title,
  testIdPrefix,
  submitLabel = DEFAULT_SUBMIT_LABEL,
  submitDisabled = false,
  size = 'default',
  summary,
  acknowledgement,
  onSend,
  children,
  onCloseAutoFocus,
}: SendEmailModalProps) {
  const showCcField = useExportEmailCcEnabled();
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [pendingCc, setPendingCc] = useState('');
  const [sending, setSending] = useState(false);
  const sentWithoutConfirmingRef = useRef(false);
  const nothingToConfirm = !showCcField && !acknowledgement && !children;

  useEffect(() => {
    if (!open) return;
    setCcEmails([]);
    setPendingCc('');
  }, [open]);

  useEffect(() => {
    if (!open) {
      sentWithoutConfirmingRef.current = false;
      return;
    }
    if (!nothingToConfirm || sentWithoutConfirmingRef.current) return;
    sentWithoutConfirmingRef.current = true;
    Promise.resolve()
      .then(() => onSend([]))
      .catch(() => undefined)
      .finally(() => onOpenChange(false));
  }, [open, nothingToConfirm, onSend, onOpenChange]);

  const halfTypedCc = showCcField && pendingCc.trim() !== '' && !isValidRecipientEmail(pendingCc.trim());

  const send = async () => {
    setSending(true);
    try {
      if ((await onSend(showCcField ? ccEmails : [])) === false) return;
    } catch {
      // The surface reports its own send failures; the modal only stays open for a retry.
      return;
    } finally {
      setSending(false);
    }
    onOpenChange(false);
  };

  const closeUnlessSending = (next: boolean) => {
    if (!next && sending) return;
    onOpenChange(next);
  };

  if (nothingToConfirm) return null;

  return (
    <Dialog open={open} onOpenChange={closeUnlessSending}>
      <DialogContent
        data-testid={`${testIdPrefix}-dialog`}
        className={cn(CONTENT_LAYER, CONTENT_WIDTH[size])}
        overlayClassName={OVERLAY_LAYER}
        aria-describedby={undefined}
        title={title}
        closeButtonProps={{ disabled: sending, 'data-testid': `${testIdPrefix}-close-button` }}
        onCloseAutoFocus={onCloseAutoFocus}
      >
        <DialogBody>
          <div className="flex flex-col gap-6 text-sm text-primary-dark">
            {children}

            {acknowledgement ? <div data-testid={`${testIdPrefix}-acknowledgement`}>{acknowledgement}</div> : null}

            {summary && summary.length > 0 ? (
              <dl data-testid={`${testIdPrefix}-summary`} className="flex flex-col gap-2">
                {summary.map((row) => (
                  <div key={row.label} className="flex flex-col gap-0.5">
                    <dt className="font-bold">{row.label}</dt>
                    <dd>{row.value}</dd>
                  </div>
                ))}
              </dl>
            ) : null}

            {showCcField ? (
              <div className="flex flex-col gap-1">
                <div id={`${testIdPrefix}-cc-label`} className="font-bold">
                  Share with others
                </div>
                <EmailRecipientsInput
                  {...recipientAddressRules}
                  value={ccEmails}
                  onChange={setCcEmails}
                  onDraftChange={setPendingCc}
                  maxRecipients={MAX_CC_RECIPIENTS}
                  placeholder="colleague@example.com"
                  testIdPrefix={`${testIdPrefix}-cc`}
                  aria-labelledby={`${testIdPrefix}-cc-label`}
                />
              </div>
            ) : null}
          </div>
          <DialogFooter className="px-0 pb-0 pt-6">
            <Button
              variant="secondary"
              data-testid={`${testIdPrefix}-cancel-button`}
              disabled={sending}
              onClick={() => closeUnlessSending(false)}
            >
              Cancel
            </Button>
            <Button
              data-testid={`${testIdPrefix}-confirm-button`}
              disabled={submitDisabled || halfTypedCc}
              loading={sending}
              tooltip={halfTypedCc ? HALF_TYPED_CC_TOOLTIP : undefined}
              onClick={send}
            >
              {submitLabel}
            </Button>
          </DialogFooter>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}
